<script setup lang="ts">
/**
 * UiAvatar — круглый аватар c initials или image (inbox-119).
 *
 * Props:
 *   name — display name; initials = first letter of первых 2 слов uppercase
 *   src  — optional image URL; если задан, рендерим <img>, иначе initials
 *   size — sm (18px) | md (36px, default) | lg (72px)
 *
 * CSS портом из docs/mockups/settings-profile.html (.avatar-big) +
 * campaigns-list.html (.avatar-mini). Circle с linear-gradient bg +
 * белые initials по центру.
 *
 * Usage:
 *   <UiAvatar name="Иван Иванов" size="lg" />
 *   <UiAvatar name="М" :src="user.photo_url" size="md" />
 *   <UiAvatar name="Team" size="sm" />
 */

import { computed } from 'vue';

interface Props {
  name?: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
}

const props = withDefaults(defineProps<Props>(), {
  name: '',
  src: null,
  size: 'md',
});

const initials = computed(() => {
  const words = (props.name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase();
});
</script>

<template>
  <div :class="['avatar', `avatar-${size}`]" :aria-label="name || undefined">
    <img v-if="src" :src="src" :alt="name" />
    <span v-else class="initials">{{ initials }}</span>
  </div>
</template>

<style scoped>
.avatar {
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: var(--gradient);
  color: #fff;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
  line-height: 1;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-sm {
  width: 18px;
  height: 18px;
  font-size: 9px;
}

.avatar-md {
  width: 36px;
  height: 36px;
  font-size: 13px;
}

.avatar-lg {
  width: 72px;
  height: 72px;
  font-size: 24px;
}
</style>
